import { Box, Table, TableBody } from '@mui/material';
import Loading from 'components/common/Loading';
import Pagination from 'components/controls/Pagination';
import { connect } from 'formik';
import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import Company from 'services/api/Company';
import { useListVisibleQuery } from 'store/app/entities/EmployeesApiSlice';
import authEmployee from 'store/selectors/authEmployee';
import EmployeeRow from './EmployeeRow';
import TableHead from './TableHead';
import Toolbar from './Toolbar';

const limit = 10;

const union = (value, added, by, base) => {
  return [
    ...value,
    ...added
      .filter(a => !value.some(v => (by ? v[by] : v) === a))
      .map(a => (by ? { ...base, [by]: a } : a)),
  ];
};

const EmployeesTable = ({ name, onChange, value, formik: { values }, isEdit }) => {
  const [order, setOrder] = React.useState({ by: 'name', isReverse: false });
  const [page, setPage] = React.useState(0);
  const [search, setSearch] = React.useState('');
  const employee = useSelector(authEmployee);
  const {
    data: { employees: employeesWithMe, metadata } = {},
    isLoading,
    isFetching,
    error,
  } = useListVisibleQuery({
    search,
    skip: page * limit,
    limit,
    order: (order.isReverse ? '-' : '') + order.by,
    group: values._id,
  });
  const employees = useMemo(
    () => employeesWithMe?.filter(e => e._id !== employee?._id),
    [employee?._id, employeesWithMe],
  );

  const selectAll = async enabled => {
    const allEmployees = await Company.getEmployeesIds(search, false);
    const membersWithoutMe = allEmployees.filter(
      e => e !== employee?._id && (enabled || value.some(v => v._id === e)),
    );
    if (enabled) {
      onChange(name, union(values[name], membersWithoutMe, '_id', { groupRole: 'member' }));
      onChange(
        `${name}Added`,
        union(values[`${name}Added`], membersWithoutMe, '_id', { groupRole: 'member' }),
      );
      onChange(
        `${name}Removed`,
        values[`${name}Removed`].filter(_id => value.some(v => v === _id)),
      );
    } else {
      onChange(
        name,
        values[name].filter(v => !membersWithoutMe.includes(v._id)),
      );
      onChange(
        `${name}Added`,
        values[`${name}Added`].filter(v => !membersWithoutMe.includes(v._id)),
      );
      onChange(
        `${name}Removed`,
        union(
          values[`${name}Removed`],
          membersWithoutMe.map(e => ({ _id: e })),
        ),
      );
    }
  };

  const setRole = (emp, groupRole) => {
    const newValue = value.map(m => (m._id === emp._id ? { ...m, groupRole } : m));
    onChange(name, newValue);
    onChange(`${name}Added`, [
      ...values[`${name}Added`].filter(e => e._id !== emp._id),
      newValue.find(e => e._id === emp._id),
    ]);
  };

  const toggleSelect = emp => {
    if (value.find(m => m._id === emp._id)) {
      onChange(
        name,
        value.filter(m => m._id !== emp._id),
      );
      onChange(
        `${name}Added`,
        values[`${name}Added`].filter(e => e._id !== emp._id),
      );
      onChange(`${name}Removed`, [...values[`${name}Removed`], emp]);
    } else {
      onChange(name, [...value, { groupRole: 'member', ...emp }]);
      onChange(`${name}Added`, [...values[`${name}Added`], { groupRole: 'member', ...emp }]);
      onChange(
        `${name}Removed`,
        values[`${name}Removed`].filter(e => e._id !== emp._id),
      );
    }
  };

  const sort = by => {
    setOrder(currentOrder => ({ by, isReverse: !currentOrder.isReverse }));
  };

  const onSearch = newSearch => {
    setSearch(newSearch);
    setPage(0);
  };

  const changePage = ({ selected }) => {
    setPage(selected);
  };

  const selectedCount = value.length;

  if (isLoading) {
    return <Loading />;
  }

  const employeesCount = metadata.total;

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        mt: 3,
        maxWidth: 'calc(100%- 100px)',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Toolbar error={error} selected={selectedCount} onSearch={onSearch} isFetching={isFetching} />
      <Box sx={{ flexGrow: 1, mb: 2 }}>
        <Table aria-labelledby="employeesTable">
          <TableHead
            isEdit={isEdit}
            search={search}
            order={order}
            selected={selectedCount}
            rows={employeesCount}
            onSort={sort}
            onSelectAll={selectAll}
          />
          <TableBody>
            {employees.map(e => (
              <EmployeeRow
                key={e._id}
                employee={e}
                member={value.find(m => m._id === e._id)}
                onSelect={toggleSelect}
                onSetRole={setRole}
                searchString={search}
                disabled={isFetching}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
      <Pagination total={employeesCount} page={page} perPage={limit} onChange={changePage} />
      {employeesCount === 0 && search ? (
        <Box
          sx={{
            fontSize: '13px',
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'center',
            paddingTop: '2em',
          }}
        >
          {I18n.t('ChannelDrawer.EmployeesTable.NotFound')}
        </Box>
      ) : null}
    </Box>
  );
};

EmployeesTable.propTypes = {
  isEdit: bool.isRequired,
  name: string.isRequired,
  value: arrayOf(string).isRequired,
  onChange: func.isRequired,
  formik: object.isRequired,
};

export default connect(EmployeesTable);
